import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {transient} from 'Application/configs/emotionTransientProps';
import {generateBgImage} from 'Application/utils/bgImages';
import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {ImageUpload, Media} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The background color of the header.
     */
    backgroundColor?: string | null | undefined;
    /**
     * The background image object, with a `url` property.
     */
    backgroundImage?: ImageUpload | undefined;
    /**
     * The type of the background.
     */
    backgroundType: 'Color' | 'Image';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Background'
     */
    testId?: string;
}

/**
 * The `Background` Component.
 *
 * @param props                 The component props.
 * @param props.backgroundColor The background color of the header.
 * @param props.backgroundImage The background image object, with a `url` property.
 * @param props.backgroundType  A boolean indicating whether a background image is present.
 * @param props.testId          A unique identifier, usually in the form of a string, assigned to the component for testing purposes.
 * @returns A React element representing the `Background` component.
 * @todo Add true documentation!
 *
 * @example
 * ```tsx
 * const MyComponent = <Background testId="myTestId">MyComponent</Background>;
 * ```
 */
const Background = observer(({
    backgroundColor,
    backgroundImage,
    backgroundType,
    testId = 'Background'
}: ComponentProps) => (
    <Wrapper
        $backgroundColor={backgroundColor}
        $backgroundImage={backgroundImage?.image}
        $backgroundType={backgroundType}
        data-cy={testId}
    />
));

Background.displayName = 'Background';

export {Background};

interface WrapperProps {
    $backgroundColor?: string | null | undefined;
    $backgroundImage?: Media | string;
    $backgroundType: 'Color' | 'Image';
}

const Wrapper = styled('section', {...transient})<WrapperProps>`
    background-color: ${({$backgroundColor, $backgroundType}) => ($backgroundType === 'Color' ? $backgroundColor : null)};
    background-image: ${({$backgroundImage, $backgroundType}) => ((isDefinedAndPopulated($backgroundImage) && $backgroundType === 'Image') ? generateBgImage($backgroundImage) : null)};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;

    ${({$backgroundImage, $backgroundType}) => ((isDefinedAndPopulated($backgroundImage) && $backgroundType === 'Image') && generateBgImage($backgroundImage))};
`;